import { datadogRum } from '@datadog/browser-rum';
import { getActivePinia, storeToRefs } from 'pinia';
import { useUserDetailsStore } from '@/stores/useUserDetails';

/**
 * Composable for logging errors and actions to Datadog RUM (Real User Monitoring).
 * Automatically enriches Datadog events with the current user's information from the user details store.
 *
 * @returns {Object} Datadog logging utilities
 * @returns {Function} addRumError - Logs an error to Datadog with user context
 * @returns {Function} addRumAction - Logs a custom action to Datadog with user context
 *
 * @example In a component or composable
 * const { addRumError, addRumAction } = useAcvDatadog();
 *
 * try {
 *   await someAsyncOperation();
 * } catch (error) {
 *   addRumError(error, { operation: 'someAsyncOperation', attemptCount: 3 });
 * }
 *
 * @example In a Pinia store action
 * async fetchData() {
 *   const { $api } = useNuxtApp();
 *   const { addRumError } = useAcvDatadog();
 *   let data = null;
 *   let error = null;
 *   try {
 *     data = await $api('/api/endpoint');
 *   } catch (err) {
 *     error = err;
 *   }
 *
 *   if (error) {
 *     addRumError(new Error('Failed to fetch data'), { endpoint: '/api/endpoint' });
 *   }
 * }
 */
const useAcvDatadog = () => {
  /**
   * Logs an error to Datadog RUM with additional context and user information.
   * The current user's details are automatically included in the error context.
   *
   * @param {Error} error - The error object to log to Datadog
   * @param {Object} [context={}] - Additional context information to attach to the error
   * @param {string} [context.endpoint] - API endpoint related to the error
   * @param {string} [context.operation] - The operation that was being performed when the error occurred
   * @param {number} [context.statusCode] - HTTP status code if applicable
   * @param {*} [context.*] - Any other relevant contextual information
   *
   * @returns {void}
   *
   * @example
   * addRumError(
   *   new Error('Failed to update user profile'),
   *   {
   *     endpoint: '/api/user/profile',
   *     statusCode: 500,
   *     attemptNumber: 2
   *   }
   * );
   */
  const addRumError = (error, context) => {
    const userDetailsStore = useUserDetailsStore(getActivePinia());
    const { user } = storeToRefs(userDetailsStore);
    if (process.env.NODE_ENV === 'development') {
      console.log(`addRumError ===> ${error}`, {
        ...context,
        user: user.value,
      });

      return;
    }
    datadogRum.addError(error, {
      ...context,
      user: user.value,
    });
  };

  /**
   * Logs a custom action to Datadog RUM with additional context and user information.
   * Use this to track important user interactions or business events that aren't automatically captured.
   *
   * @param {string} action - The name/description of the action being logged
   * @param {Object} [context={}] - Additional context information to attach to the action
   * @param {string} [context.feature] - The feature area where the action occurred
   * @param {string} [context.category] - Category of the action (e.g., 'navigation', 'form-submit', 'search')
   * @param {*} [context.*] - Any other relevant contextual information
   *
   * @returns {void}
   *
   * @example
   * addRumAction(
   *   'Job Search Performed',
   *   {
   *     feature: 'job-search',
   *     filters: { location: 'New York', status: 'active' },
   *     resultCount: 42
   *   }
   * );
   *
   * @example
   * addRumAction(
   *   'Export CSV',
   *   {
   *     feature: 'manage-jobs',
   *     recordCount: 150,
   *     fileSize: '2.3MB'
   *   }
   * );
   */
  const addRumAction = (action, context) => {
    const userDetailsStore = useUserDetailsStore(getActivePinia());
    const { user } = storeToRefs(userDetailsStore);
    if (process.env.NODE_ENV === 'development') {
      console.log(`addRumAction ===> ${action}`, {
        ...context,
        user: user.value,
      });
      return;
    }
    datadogRum.addAction(action, {
      ...context,
      user: user.value,
    });
  };

  return {
    addRumError,
    addRumAction,
  };
};

export default useAcvDatadog;
